import { useEffect, useRef, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangleIcon, CheckCircle2Icon, SendIcon, ShieldAlertIcon } from 'lucide-react';
import {
  detectStructuredPii,
  type IncidentGegevenssoort,
  type IncidentGroep,
  type StructuredPiiType,
  type SubmitSecurityIncidentInput,
} from '@kennisbank/shared';
import { Layout } from '../components/Layout.js';
import { useAuth } from '../auth/AuthProvider.js';
import { ApiError, apiPost } from '../api/client.js';

/**
 * Melding informatiebeveiligingsincident (plan §4/§5/§8). Neemt het Microsoft
 * Forms-formulier "Melden (mogelijk) datalek" 1-op-1 over: elf vragen, zelfde
 * volgorde, zelfde verplicht/optioneel-verdeling.
 *
 * Leidend ontwerpcriterium uit het plan: bij elke keuze wint de variant die de
 * drempel verlaagt. Daarom raakt een mislukte verzending nooit ingevulde tekst
 * kwijt, en waarschuwt de PII-controle op vraag 3 alleen (zie piiWaarschuwing);
 * een melding die niet verstuurd kan worden is erger dan een BSN in een
 * beheerdersscherm. Dat wijkt bewust af van Mila, waar trap 1 hard blokkeert.
 */

interface SubmitResponse {
  id: string;
  /** Zet de backend een leesbaar nummer, dan tonen we dat; anders de id. */
  meldingsnummer?: string;
  status?: string;
}

const GROEP_OPTIES: { value: IncidentGroep; label: string }[] = [
  { value: 'patient', label: 'Patiënten of cliënten' },
  { value: 'medewerker', label: 'Medewerkers' },
  { value: 'anders', label: 'Anders' },
  { value: 'geen_of_onbekend', label: 'Geen of nog niet bekend' },
];

const GEGEVENSSOORT_OPTIES: { value: IncidentGegevenssoort; label: string }[] = [
  { value: 'gezondheid', label: 'Gegevens over gezondheid' },
  {
    value: 'politiek_levensbeschouwelijk',
    label: 'Gegevens over politieke opvattingen, godsdienst of levensovertuiging',
  },
  {
    value: 'seksueel_gedrag_gerichtheid',
    label: 'Gegevens over seksueel gedrag of seksuele gerichtheid',
  },
  { value: 'etnische_afkomst', label: 'Gegevens over ras of etnische afkomst' },
  {
    value: 'administratief',
    label:
      'Administratieve gegevens, zoals BSN, paspoort- of identiteitsnummer, rekeningnummer of loon',
  },
  { value: 'geen_of_onbekend', label: 'Geen of nog niet bekend' },
];

const PII_LABELS: Record<StructuredPiiType, string> = {
  bsn: 'een BSN',
  telefoonnummer: 'een telefoonnummer',
  email: 'een e-mailadres',
  postcode_huisnummer: 'een postcode met huisnummer',
};

/** "Geen of nog niet bekend" sluit de andere antwoorden uit, en andersom. */
function toggleExclusief<T extends string>(huidig: T[], waarde: T, exclusief: T): T[] {
  const aan = huidig.includes(waarde);
  if (waarde === exclusief) return aan ? [] : [exclusief];
  if (aan) return huidig.filter((v) => v !== waarde);
  return [...huidig.filter((v) => v !== exclusief), waarde];
}

function vandaag(): string {
  // sv-SE levert JJJJ-MM-DD in de lokale tijdzone (Europe/Amsterdam), zodat de
  // max-grens van de datumvelden 's avonds niet een dag vooruit springt.
  return new Date().toLocaleDateString('sv-SE');
}

const invoerKlassen =
  'w-full rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-[14px] text-ink-900 placeholder:text-ink-900/35 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400';

function Vraag({
  nummer,
  titel,
  verplicht,
  hint,
  fout,
  htmlFor,
  children,
}: {
  nummer: number;
  titel: string;
  verplicht: boolean;
  hint?: ReactNode;
  fout?: string;
  htmlFor?: string;
  children: ReactNode;
}): JSX.Element {
  const label = (
    <>
      <span className="mr-1.5 text-ink-900/45">{nummer}.</span>
      {titel}{' '}
      <span className="font-normal text-ink-900/45">
        {verplicht ? '(verplicht)' : '(optioneel)'}
      </span>
    </>
  );
  return (
    <div className="rounded-2xl border border-brand-100 bg-white p-5 sm:p-6">
      {htmlFor ? (
        <label htmlFor={htmlFor} className="block text-[14px] font-semibold text-ink-900">
          {label}
        </label>
      ) : (
        <p className="text-[14px] font-semibold text-ink-900">{label}</p>
      )}
      {hint && <div className="mt-1 text-[12px] leading-relaxed text-ink-900/55">{hint}</div>}
      <div className="mt-3">{children}</div>
      {fout && (
        <p className="mt-2 text-[12px] font-medium text-error" role="alert">
          {fout}
        </p>
      )}
    </div>
  );
}

export function IncidentMeldenPage(): JSX.Element {
  const { user } = useAuth();

  // Vraag 1 t/m 11, in de volgorde van het formulier.
  const [melderNaam, setMelderNaam] = useState('');
  const [contactOptIn, setContactOptIn] = useState(false);
  const [ontdekkingsWijze, setOntdekkingsWijze] = useState('');
  const [omschrijving, setOmschrijving] = useState('');
  const [datumLek, setDatumLek] = useState('');
  const [datumOntdekt, setDatumOntdekt] = useState('');
  const [groepen, setGroepen] = useState<IncidentGroep[]>([]);
  const [groepenAnders, setGroepenAnders] = useState('');
  const [gegevenssoorten, setGegevenssoorten] = useState<IncidentGegevenssoort[]>([]);
  const [andereOrganisaties, setAndereOrganisaties] = useState<boolean | null>(null);
  const [organisatiesWelke, setOrganisatiesWelke] = useState('');
  const [ondernomenActie, setOndernomenActie] = useState('');
  const [voorgesteldeMaatregelen, setVoorgesteldeMaatregelen] = useState('');

  const [fouten, setFouten] = useState<Record<string, string>>({});
  const [verstuurFout, setVerstuurFout] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);
  const [resultaat, setResultaat] = useState<{ nummer: string; contactOptIn: boolean } | null>(
    null,
  );
  // Trap 1 van de PII-controle op vraag 3: waarschuwen en bevestigen, nooit
  // blokkeren (plan §5). Zolang dit gevuld is wacht de melding op één klik.
  const [piiWaarschuwing, setPiiWaarschuwing] = useState<StructuredPiiType[] | null>(null);

  // Vraag 1 komt vooringevuld uit het profiel maar blijft wisbaar: melden
  // namens een ander of anoniem moet mogelijk blijven. Zodra de melder het
  // veld zelf aanraakt, vult het profiel niets meer aan.
  const naamAangeraakt = useRef(false);
  useEffect(() => {
    if (user && !naamAangeraakt.current) setMelderNaam(user.displayName);
  }, [user]);

  function valideer(): Record<string, string> {
    const f: Record<string, string> = {};
    if (!melderNaam.trim()) f.melderNaam = 'Vul je naam in, of vul "anoniem" in.';
    if (!datumLek) f.datumLek = 'Kies de datum van het (mogelijke) datalek.';
    if (!datumOntdekt) f.datumOntdekt = 'Kies de datum waarop het is ontdekt.';
    if (datumLek && datumOntdekt && datumOntdekt < datumLek) {
      f.datumOntdekt = 'De ontdekkingsdatum kan niet vóór de datum van het incident liggen.';
    }
    if (groepen.length === 0) f.groepen = 'Kies minimaal één antwoord.';
    if (gegevenssoorten.length === 0) f.gegevenssoorten = 'Kies minimaal één antwoord.';
    if (andereOrganisaties === null) f.andereOrganisaties = 'Kies ja of nee.';
    if (andereOrganisaties === true && !organisatiesWelke.trim()) {
      f.organisatiesWelke = 'Geef aan welke andere organisaties betrokken zijn.';
    }
    if (!ondernomenActie.trim()) {
      f.ondernomenActie =
        'Beschrijf wat je zelf al hebt gedaan. Weet je het niet, vul dan "niets" in.';
    }
    return f;
  }

  async function verstuur(): Promise<void> {
    setBezig(true);
    setVerstuurFout(null);
    const payload: SubmitSecurityIncidentInput = {
      melderNaam: melderNaam.trim(),
      melderContactOptIn: contactOptIn,
      ...(ontdekkingsWijze.trim() ? { ontdekkingsWijze: ontdekkingsWijze.trim() } : {}),
      ...(omschrijving.trim() ? { omschrijving: omschrijving.trim() } : {}),
      datumLek,
      datumOntdekt,
      groepen,
      ...(groepen.includes('anders') && groepenAnders.trim()
        ? { groepenAnders: groepenAnders.trim() }
        : {}),
      gegevenssoorten,
      andereOrganisaties: andereOrganisaties === true,
      ...(andereOrganisaties === true ? { organisatiesWelke: organisatiesWelke.trim() } : {}),
      ondernomenActie: ondernomenActie.trim(),
      ...(voorgesteldeMaatregelen.trim()
        ? { voorgesteldeMaatregelen: voorgesteldeMaatregelen.trim() }
        : {}),
    };
    try {
      const res = await apiPost<SubmitResponse>('/api/v1/incidents', payload);
      setResultaat({ nummer: res.meldingsnummer ?? res.id, contactOptIn });
      window.scrollTo({ top: 0 });
    } catch (err) {
      // De ingevulde velden blijven staan: wie net een incident meldt mag zijn
      // tekst niet kwijtraken aan een haperend netwerk.
      if (err instanceof ApiError && (err.status === 404 || err.status === 401)) {
        // 404 = feature-flag INCIDENT_MELDING_ENABLED staat uit; 401 = sessie
        // verlopen tijdens het invullen. In beide gevallen helpt "probeer
        // opnieuw" de melder niet, maar mag de melding niet blijven liggen.
        setVerstuurFout(
          'Je melding kon niet worden opgeslagen omdat je niet (meer) bent ingelogd of het meldformulier op dit moment niet beschikbaar is. Je antwoorden staan nog in het formulier. Log opnieuw in en verstuur de melding nogmaals, of meld het incident telefonisch bij Linda Blank.',
        );
      } else if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
        setVerstuurFout(
          `${err.message} Je antwoorden staan nog in het formulier; pas ze aan en probeer het opnieuw.`,
        );
      } else {
        setVerstuurFout(
          'Versturen is niet gelukt. Je antwoorden staan nog in het formulier; probeer het over een minuut opnieuw. Blijft het misgaan, meld het incident dan telefonisch bij Linda Blank.',
        );
      }
    } finally {
      setBezig(false);
    }
  }

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    if (bezig) return;
    const f = valideer();
    setFouten(f);
    setVerstuurFout(null);
    if (Object.keys(f).length > 0) {
      setPiiWaarschuwing(null);
      document.getElementById('incident-fout-samenvatting')?.scrollIntoView({ block: 'center' });
      return;
    }
    // Waarschuwen, niet blokkeren: dezelfde detectStructuredPii-aanroep als de
    // ask-route achter Mila, maar het antwoord is een bevestigingsvraag en de
    // melding gaat daarna gewoon door.
    const treffers = detectStructuredPii(omschrijving);
    if (treffers.length > 0) {
      setPiiWaarschuwing([...new Set(treffers.map((t) => t.type))]);
      return;
    }
    void verstuur();
  }

  if (resultaat) {
    return (
      <Layout>
        <div className="mx-auto max-w-2xl px-4 py-8 sm:px-5">
          <div className="rounded-2xl border border-brand-100 bg-white p-6">
            <h1 className="flex items-center gap-2 text-[20px] font-bold text-ink-900">
              <CheckCircle2Icon size={20} className="shrink-0 text-success" aria-hidden="true" />
              Je melding is ontvangen
            </h1>
            <p className="mt-3 text-[14px] leading-relaxed text-ink-900/70">
              Dank voor het melden. Je meldingsnummer is:
            </p>
            <p className="mt-2 break-all rounded-lg bg-brand-50 px-3 py-2 font-mono text-[13px] text-ink-900">
              {resultaat.nummer}
            </p>
            <h2 className="mt-6 text-[14px] font-semibold text-ink-900">Wat gebeurt er nu?</h2>
            <ul className="mt-2 list-disc space-y-1.5 pl-5 text-[14px] leading-relaxed text-ink-900/70">
              <li>Je melding staat geregistreerd en is meteen zichtbaar voor de beheerder.</li>
              <li>
                Linda Blank, de privacy-contactpersoon van SUPPOHRT, bekijkt je melding en
                beoordeelt of het om een datalek gaat. Dat gebeurt binnen één werkdag.
              </li>
              <li>
                {resultaat.contactOptIn
                  ? 'Zij neemt binnen één werkdag contact met je op, ook als er verder niets hoeft te gebeuren.'
                  : 'Je hebt geen toestemming gegeven om je accountgegevens te gebruiken. Er wordt alleen contact met je opgenomen als je zelf contactgegevens in je melding hebt achtergelaten.'}
              </li>
              <li>
                Blijkt het een datalek, dan geldt een wettelijke termijn van 72 uur. Het kan zijn
                dat je nog aanvullende vragen krijgt.
              </li>
              <li>Je hoeft zelf verder niets te doen.</li>
            </ul>
            <p className="mt-4 text-[13px] leading-relaxed text-ink-900/55">
              Gaat het om een acuut probleem dat nog gaande is, wacht dan niet op een reactie en bel
              Linda Blank direct.
            </p>
            <div className="mt-6 flex flex-col gap-2 sm:flex-row">
              <Link
                to="/"
                className="inline-flex w-full items-center justify-center rounded-lg bg-brand-600 px-4 py-2.5 text-[14px] font-medium text-white hover:bg-brand-700 sm:w-auto"
              >
                Terug naar de startpagina
              </Link>
              <button
                type="button"
                onClick={() => {
                  setResultaat(null);
                  setOntdekkingsWijze('');
                  setOmschrijving('');
                  setDatumLek('');
                  setDatumOntdekt('');
                  setGroepen([]);
                  setGroepenAnders('');
                  setGegevenssoorten([]);
                  setAndereOrganisaties(null);
                  setOrganisatiesWelke('');
                  setOndernomenActie('');
                  setVoorgesteldeMaatregelen('');
                  setFouten({});
                  setPiiWaarschuwing(null);
                }}
                className="inline-flex w-full items-center justify-center rounded-lg border border-brand-200 px-4 py-2.5 text-[14px] font-medium text-ink-900/70 hover:bg-brand-50 sm:w-auto"
              >
                Nog een melding doen
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const foutenLijst = Object.values(fouten);

  return (
    <Layout>
      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-5">
        <header className="mb-6">
          <h1 className="flex items-center gap-2 text-[20px] font-bold text-ink-900">
            <ShieldAlertIcon size={20} className="shrink-0 text-brand-600" aria-hidden="true" />
            Melding informatiebeveiligingsincident
          </h1>
          <p className="mt-3 text-[14px] leading-relaxed text-ink-900/70">
            Gebruik dit formulier als er iets is misgegaan met gegevens. Bijvoorbeeld een mail die
            naar de verkeerde persoon ging, een kwijtgeraakte telefoon of een kwijtgeraakt papieren
            dossier, een phishingmail, of het vermoeden dat iemand gegevens heeft ingezien die niet
            voor hem of haar bedoeld waren.
          </p>
          <p className="mt-3 text-[14px] leading-relaxed text-ink-900/70">
            Twijfel je of het ernstig genoeg is? Meld het dan toch. Beoordelen of het een datalek is
            doen wij, dat hoef je niet te doen.
          </p>
          <div className="mt-4 rounded-2xl border border-mosterdgeel-100 bg-mosterdgeel-10 p-4 text-[13px] leading-relaxed text-ink-900/75">
            <p>
              Dank voor het melden van een (mogelijk) datalek. Je melding komt terecht bij Linda
              Blank, de privacy-contactpersoon van SUPPOHRT. Zij neemt contact met je op en bepaalt
              samen met jou welke vervolgstappen nodig zijn.
            </p>
            <p className="mt-2">
              Er worden geen gegevens automatisch verzameld: alleen wat je hieronder zelf invult
              wordt vastgelegd.
            </p>
          </div>
        </header>

        <form onSubmit={onSubmit} noValidate className="space-y-4">
          {/* Vraag 1 */}
          <Vraag
            nummer={1}
            titel="Wat is je naam?"
            verplicht
            htmlFor="incident-naam"
            fout={fouten.melderNaam}
          >
            <input
              id="incident-naam"
              type="text"
              value={melderNaam}
              onChange={(e) => {
                naamAangeraakt.current = true;
                setMelderNaam(e.target.value);
              }}
              maxLength={200}
              autoComplete="name"
              className={invoerKlassen}
            />
            <p className="mt-2 text-[12px] leading-relaxed text-ink-900/55">
              Je naam is vast ingevuld vanuit je profiel. Je mag hem wissen of vervangen,
              bijvoorbeeld door de naam van de collega voor wie je meldt.{' '}
              <button
                type="button"
                onClick={() => {
                  naamAangeraakt.current = true;
                  setMelderNaam('anoniem');
                }}
                className="font-medium text-brand-700 underline hover:text-brand-800"
              >
                Anoniem melden
              </button>
            </p>
            <label className="mt-3 flex cursor-pointer items-start gap-2 text-[13px] leading-relaxed text-ink-900/75">
              <input
                type="checkbox"
                checked={contactOptIn}
                onChange={(e) => setContactOptIn(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-brand-600"
              />
              SUPPOHRT mag mijn accountgegevens gebruiken om contact met mij op te nemen.
            </label>
          </Vraag>

          {/* Vraag 2 */}
          <Vraag
            nummer={2}
            titel="Hoe is het (mogelijke) datalek ontdekt?"
            verplicht={false}
            htmlFor="incident-ontdekking"
          >
            <textarea
              id="incident-ontdekking"
              value={ontdekkingsWijze}
              onChange={(e) => setOntdekkingsWijze(e.target.value)}
              rows={3}
              maxLength={5000}
              className={invoerKlassen}
            />
          </Vraag>

          {/* Vraag 3 — PII-waarschuwing, geen blokkade (plan §5). */}
          <Vraag
            nummer={3}
            titel="Wat is er gebeurd?"
            verplicht={false}
            htmlFor="incident-omschrijving"
            hint="Beschrijf wat er gebeurde zonder namen, BSN's of telefoonnummers. Hoe gedetailleerder, hoe beter, maar houd het anoniem."
          >
            <textarea
              id="incident-omschrijving"
              value={omschrijving}
              onChange={(e) => {
                setOmschrijving(e.target.value);
                if (piiWaarschuwing) setPiiWaarschuwing(null);
              }}
              rows={5}
              maxLength={5000}
              className={invoerKlassen}
            />
          </Vraag>

          {/* Vraag 4 */}
          <Vraag
            nummer={4}
            titel="Op welke datum vond het (mogelijke) datalek plaats?"
            verplicht
            htmlFor="incident-datum-lek"
            fout={fouten.datumLek}
            hint="Weet je de precieze dag niet, kies dan de dag die er het dichtst bij zit."
          >
            <input
              id="incident-datum-lek"
              type="date"
              value={datumLek}
              max={vandaag()}
              onChange={(e) => setDatumLek(e.target.value)}
              className={`${invoerKlassen} sm:max-w-[220px]`}
            />
          </Vraag>

          {/* Vraag 5 */}
          <Vraag
            nummer={5}
            titel="Op welke datum is het ontdekt?"
            verplicht
            htmlFor="incident-datum-ontdekt"
            fout={fouten.datumOntdekt}
            hint="Deze datum kan niet vóór de datum bij vraag 4 liggen."
          >
            <input
              id="incident-datum-ontdekt"
              type="date"
              value={datumOntdekt}
              min={datumLek || undefined}
              max={vandaag()}
              onChange={(e) => setDatumOntdekt(e.target.value)}
              className={`${invoerKlassen} sm:max-w-[220px]`}
            />
          </Vraag>

          {/* Vraag 6 */}
          <Vraag
            nummer={6}
            titel="Van welke groepen personen zijn persoonsgegevens gelekt?"
            verplicht
            fout={fouten.groepen}
            hint="Meerdere antwoorden mogelijk."
          >
            <div className="flex flex-col gap-2">
              {GROEP_OPTIES.map((o) => (
                <label
                  key={o.value}
                  className="flex cursor-pointer items-start gap-2 text-[14px] leading-relaxed text-ink-900/80"
                >
                  <input
                    type="checkbox"
                    checked={groepen.includes(o.value)}
                    onChange={() =>
                      setGroepen((prev) => toggleExclusief(prev, o.value, 'geen_of_onbekend'))
                    }
                    className="mt-0.5 h-4 w-4 shrink-0 accent-brand-600"
                  />
                  {o.label}
                </label>
              ))}
            </div>
            {groepen.includes('anders') && (
              <input
                type="text"
                value={groepenAnders}
                onChange={(e) => setGroepenAnders(e.target.value)}
                maxLength={500}
                placeholder="Om welke groep gaat het?"
                aria-label="Anders, namelijk"
                className={`${invoerKlassen} mt-3`}
              />
            )}
          </Vraag>

          {/* Vraag 7 */}
          <Vraag
            nummer={7}
            titel="Welke bijzondere persoonsgegevens zijn gelekt?"
            verplicht
            fout={fouten.gegevenssoorten}
            hint="Meerdere antwoorden mogelijk."
          >
            <div className="flex flex-col gap-2">
              {GEGEVENSSOORT_OPTIES.map((o) => (
                <label
                  key={o.value}
                  className="flex cursor-pointer items-start gap-2 text-[14px] leading-relaxed text-ink-900/80"
                >
                  <input
                    type="checkbox"
                    checked={gegevenssoorten.includes(o.value)}
                    onChange={() =>
                      setGegevenssoorten((prev) =>
                        toggleExclusief(prev, o.value, 'geen_of_onbekend'),
                      )
                    }
                    className="mt-0.5 h-4 w-4 shrink-0 accent-brand-600"
                  />
                  {o.label}
                </label>
              ))}
            </div>
          </Vraag>

          {/* Vraag 8 */}
          <Vraag
            nummer={8}
            titel="Zijn er andere organisaties bij betrokken?"
            verplicht
            fout={fouten.andereOrganisaties}
          >
            <div className="flex gap-2">
              {[
                { waarde: true, label: 'Ja' },
                { waarde: false, label: 'Nee' },
              ].map((o) => (
                <label
                  key={o.label}
                  className={`flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-[14px] transition-colors sm:flex-none sm:justify-start ${
                    andereOrganisaties === o.waarde
                      ? 'border-brand-500 bg-brand-50 text-ink-900'
                      : 'border-brand-100 text-ink-900/70 hover:bg-brand-50/60'
                  }`}
                >
                  <input
                    type="radio"
                    name="incident-andere-organisaties"
                    checked={andereOrganisaties === o.waarde}
                    onChange={() => setAndereOrganisaties(o.waarde)}
                    className="h-4 w-4 accent-brand-600"
                  />
                  {o.label}
                </label>
              ))}
            </div>
          </Vraag>

          {/* Vraag 9 — alleen zichtbaar én verplicht bij "ja" op vraag 8. */}
          {andereOrganisaties === true && (
            <Vraag
              nummer={9}
              titel="Zo ja, welke organisaties?"
              verplicht
              htmlFor="incident-organisaties"
              fout={fouten.organisatiesWelke}
            >
              <input
                id="incident-organisaties"
                type="text"
                value={organisatiesWelke}
                onChange={(e) => setOrganisatiesWelke(e.target.value)}
                maxLength={1000}
                className={invoerKlassen}
              />
            </Vraag>
          )}

          {/* Vraag 10 */}
          <Vraag
            nummer={10}
            titel="Welke actie heb je zelf al ondernomen?"
            verplicht
            htmlFor="incident-actie"
            fout={fouten.ondernomenActie}
            hint="Bijvoorbeeld: de mail teruggeroepen, de ontvanger gebeld, het wachtwoord gewijzigd. Heb je nog niets gedaan, vul dan in wat je al wel weet."
          >
            <textarea
              id="incident-actie"
              value={ondernomenActie}
              onChange={(e) => setOndernomenActie(e.target.value)}
              rows={4}
              maxLength={5000}
              className={invoerKlassen}
            />
          </Vraag>

          {/* Vraag 11 */}
          <Vraag
            nummer={11}
            titel="Welke maatregelen zijn volgens jou nodig om dit in de toekomst te voorkomen?"
            verplicht={false}
            htmlFor="incident-maatregelen"
          >
            <textarea
              id="incident-maatregelen"
              value={voorgesteldeMaatregelen}
              onChange={(e) => setVoorgesteldeMaatregelen(e.target.value)}
              rows={3}
              maxLength={5000}
              className={invoerKlassen}
            />
          </Vraag>

          <div id="incident-fout-samenvatting">
            {foutenLijst.length > 0 && (
              <div
                className="rounded-2xl border border-error/30 bg-error/5 p-4 text-[13px] leading-relaxed text-error"
                role="alert"
              >
                <p className="font-semibold">Er ontbreekt nog iets:</p>
                <ul className="mt-1.5 list-disc space-y-1 pl-5">
                  {foutenLijst.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {piiWaarschuwing && (
            <div
              className="rounded-2xl border border-mosterdgeel-100 bg-mosterdgeel-10 p-4 text-[13px] leading-relaxed text-ink-900/80"
              role="alert"
            >
              <div className="flex items-start gap-2">
                <AlertTriangleIcon
                  size={18}
                  className="mt-0.5 shrink-0 text-mosterdgeel-600"
                  aria-hidden="true"
                />
                <div>
                  <p>
                    Je omschrijving bij vraag 3 lijkt{' '}
                    {piiWaarschuwing.map((t) => PII_LABELS[t]).join(' en ')} te bevatten. Weet je
                    zeker dat je dit wilt meesturen?
                  </p>
                  <p className="mt-1.5 text-ink-900/60">
                    Het mag als het nodig is om het incident te begrijpen. De melding is alleen
                    zichtbaar voor de beheerder. Kan het weg, dan is dat netter.
                  </p>
                </div>
              </div>
              <div className="mt-3 flex flex-col gap-2 pl-7 sm:flex-row">
                <button
                  type="button"
                  onClick={() => {
                    setPiiWaarschuwing(null);
                    document.getElementById('incident-omschrijving')?.focus();
                  }}
                  className="rounded-lg border border-ink-900/20 px-3 py-2 text-[13px] font-semibold text-ink-900/75 hover:bg-white/60"
                >
                  Tekst aanpassen
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPiiWaarschuwing(null);
                    void verstuur();
                  }}
                  className="rounded-lg bg-brand-600 px-3 py-2 text-[13px] font-semibold text-white hover:bg-brand-700"
                >
                  Ja, zo versturen
                </button>
              </div>
            </div>
          )}

          {verstuurFout && (
            <div
              className="rounded-2xl border border-error/30 bg-error/5 p-4 text-[13px] leading-relaxed text-error"
              role="alert"
            >
              {verstuurFout}
            </div>
          )}

          <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center">
            <button
              type="submit"
              disabled={bezig}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand-600 px-5 py-3 text-[15px] font-semibold text-white hover:bg-brand-700 disabled:opacity-50 sm:w-auto"
            >
              <SendIcon size={15} aria-hidden="true" />
              {bezig ? 'Bezig met versturen…' : 'Melding versturen'}
            </button>
            <p className="text-[12px] leading-relaxed text-ink-900/55">
              Na het versturen zie je een meldingsnummer en lees je wat er verder gebeurt.
            </p>
          </div>
        </form>
      </div>
    </Layout>
  );
}
